import ModalWrapper from 'components/ModalWrapper';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { Permission } from 'interfaces/permissionOption';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useRefreshStudyUploadList } from 'utils/queryHooks/useStudyUploadList';
import { usePermissions } from 'utils/usePermissions';
import UploadSlidesForm from './UploadSlidesForm';
import { UploadSlidesOpenedAtProvider, useUploadSlidesOpenedAtState } from './UploadSlidesOpenedAtContext';

interface Props {
  studyId: string;
  onClose: () => void;
  isOpen: boolean;
}

const UploadSlidesModal: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { onClose: onCloseFromHost, isOpen, studyId } = props;
  const navigate = useNavigate();
  const confirmWithModal = useConfirmation();
  const refreshStudyUploadList = useRefreshStudyUploadList();
  const openedAt = useUploadSlidesOpenedAtState(isOpen);

  const onClose = React.useCallback(() => {
    onCloseFromHost();
    if (studyId) {
      refreshStudyUploadList(studyId);
    }
  }, [onCloseFromHost, refreshStudyUploadList, studyId]);

  const { hasPermission } = usePermissions();

  const canRunDemo = hasPermission(Permission.UseNucleaiExperimentalFeatures);
  const handleImplicitClose = React.useCallback(async () => {
    if (
      await confirmWithModal({
        title: 'Close upload slides?',
        text: 'Your current choices will be cleared.',
        confirmButtonProps: { title: 'Close' },
      })
    ) {
      onClose();
    }
  }, [confirmWithModal, onClose]);

  React.useEffect(() => {
    if (canRunDemo) {
      const onCtrrlN = (event: KeyboardEvent) => {
        if (event.ctrlKey && (event.key === 'n' || event.key === 'N' || event.key === 'מ')) {
          event.preventDefault();
          onClose();
          navigate({
            pathname: '/procedures',
            search: 'labId=cc66db90-1e3e-11e9-80b7-0af5642e5b64&studyId=5c323dbc-3373-11ed-8f20-6dd71ae56152',
          });
        }
      };
      document.addEventListener('keydown', onCtrrlN, false);

      return () => {
        document.removeEventListener('keydown', onCtrrlN, false);
      };
    }
  }, [canRunDemo, onClose, navigate]);

  return (
    <ModalWrapper
      onClose={handleImplicitClose}
      isOpen={isOpen}
      closeOnDialogClick={false}
      closeOnEscapeKeyDown={false}
      fullWidth={true}
      maxWidth="lg"
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: 'calc(100vh - 64px)',
          overflow: 'hidden',
        },
      }}
      contentSx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        p: 0,
      }}
    >
      <UploadSlidesOpenedAtProvider openedAt={openedAt}>
        <UploadSlidesForm {...props} onClose={onClose} />
      </UploadSlidesOpenedAtProvider>
    </ModalWrapper>
  );
};

export default UploadSlidesModal;
